// Mock data for plan performance dashboard

export const generateMockPerformanceData = (planId) => {
  // Generate realistic but random data for the plan
  return {
    overview: {
      totalPatients: Math.floor(Math.random() * 80) + 20,
      compliantPatients: Math.floor(Math.random() * 60) + 10,
      nonCompliantPatients: Math.floor(Math.random() * 30) + 5,
      alertCount: Math.floor(Math.random() * 15),
      complianceRate: Math.floor(Math.random() * 40) + 60, // 60-100%
      lastUpdated: new Date().toISOString()
    },
    complianceTrend: Array.from({ length: 12 }, (_, i) => {
      // Create last 12 months trend with realistic but random data
      const month = new Date()
      month.setMonth(month.getMonth() - 11 + i)

      // Start with a baseline and add some random variation
      let baseCompliance = 70 + i * 1.5 // Trend slightly upward over time
      if (baseCompliance > 95) baseCompliance = 95

      // Add some random variation
      const randomVariation = Math.random() * 15 - 7.5 // -7.5 to +7.5
      const compliance = Math.min(100, Math.max(50, baseCompliance + randomVariation))

      return {
        month: month.toISOString().slice(0, 7), // YYYY-MM format
        compliance: Math.round(compliance),
        target: 85
      }
    }),
    testCompliance: {
      // Different test types and their compliance rates
      bloodPressure: Math.floor(Math.random() * 30) + 70,
      bloodGlucose: Math.floor(Math.random() * 30) + 65,
      cholesterol: Math.floor(Math.random() * 30) + 60,
      hba1c: Math.floor(Math.random() * 30) + 75,
      renalFunction: Math.floor(Math.random() * 30) + 55,
      eyeExam: Math.floor(Math.random() * 40) + 50
    },
    visitCompliance: {
      scheduledVisits: Math.floor(Math.random() * 50) + 150,
      completedVisits: Math.floor(Math.random() * 40) + 110,
      missedVisits: Math.floor(Math.random() * 30) + 20,
      upcomingVisits: Math.floor(Math.random() * 20) + 10
    },
    nonCompliantPatients: Array.from({ length: Math.floor(Math.random() * 15) + 5 }, (_, i) => {
      // Generate list of non-compliant patients
      return {
        id: `PAT-${1000 + i}`,
        name: `Patient ${1000 + i}`,
        lastVisit: (() => {
          const date = new Date()
          date.setDate(date.getDate() - Math.floor(Math.random() * 120) - 30) // 30-150 days ago
          return date.toISOString().slice(0, 10)
        })(),
        missedTests: (() => {
          const tests = ['Blood Pressure', 'Blood Glucose', 'Cholesterol', 'HbA1c', 'Renal Function', 'Eye Exam']
          return tests.filter(() => Math.random() > 0.6)
        })(),
        missedVisits: Math.floor(Math.random() * 3) + 1,
        riskLevel: (() => {
          const levels = ['Low', 'Medium', 'High', 'Critical']
          const weights = [0.2, 0.3, 0.3, 0.2] // Probabilities for each level
          const random = Math.random()
          let sum = 0

          for (let i = 0; i < levels.length; i++) {
            sum += weights[i]
            if (random <= sum) return levels[i]
          }

          return levels[0] // Default to low if something goes wrong
        })()
      }
    })
  }
}

// Sample alerts for the plan
export const generateMockAlerts = (planId) => {
  const alertTypes = [
    'Patient missed scheduled visit',
    'Test compliance below threshold',
    'Multiple patients at high risk',
    'Visit compliance trend decreasing',
    'HbA1c test overdue for multiple patients',
    'Blood pressure readings consistently elevated'
  ]

  return Array.from({ length: Math.floor(Math.random() * 8) + 3 }, (_, i) => {
    const type = alertTypes[Math.floor(Math.random() * alertTypes.length)]
    const date = new Date()
    date.setDate(date.getDate() - Math.floor(Math.random() * 14)) // 0-14 days ago

    return {
      id: `ALERT-${1000 + i}`,
      type,
      description: `${type} detected - requires attention`,
      date: date.toISOString(),
      severity: Math.random() > 0.7 ? 'High' : Math.random() > 0.4 ? 'Medium' : 'Low',
      resolved: Math.random() > 0.7 // 30% resolved
    }
  }).sort((a, b) => {
    // Sort by unresolved first, then by severity, then by date
    if (a.resolved !== b.resolved) return a.resolved ? 1 : -1

    const severityWeight = { High: 3, Medium: 2, Low: 1 }
    if (a.severity !== b.severity) return severityWeight[b.severity] - severityWeight[a.severity]

    return new Date(b.date) - new Date(a.date)
  })
}
